<template>
    <div>
        <v-row justify="center" class="bg-color-teal-900">
            <v-col cols="0" sm="2"></v-col>
            <v-col cols="12" sm="8">
                <v-row>
                    <v-container>
                        <div class="body-text text-white mt-4">
                            <p class="text-h4 font-weight-light">
                                <LanguagePlaceholder is-html
                                    default-text="Redemption Balance Report" />
                            </p>
                            <br />
                            <p>
                                <b>What is this report: </b>
                                This report shows the rewards value your partners have earned during the quarter (<b>opening balance</b>), any <b>banked rewards</b> carried over from the previous quarter, their <b>total available rewards</b>, the amount they have <b>redeemed (converted to E-vouchers)</b>, and their <b>remaining balance</b>. This is a <b>live report</b>.
                                <br /><br />
                                <b style="font-style: italic;">What are banked rewards: </b>
                                Banked rewards are rewards <b>carried over to the next incentive period</b> when a Partner earns less than <b>$50</b>. If their combined balance still doesn’t reach <b>$50</b> in the next period, the reward <b>expires</b>. Rewards can only be <b>banked once</b>.
                                <br /><br />
                                <b>How do you use it: </b>
                                Use this report <b>during the open redemption period</b> to encourage partners to spend with you. A partner will only appear on the report when their <b>opening balance plus banked rewards is $50 or more</b>. Once their balance reaches <b>zero</b>, they will no longer appear on the report.
                            </p>
                            <PleaseNote></PleaseNote>
                        </div>
                    </v-container>

                    <v-row class="my-2">
                        <v-col cols="12" sm="6" md="4">
                            <div>
                                <v-select class="text-white" v-model="selectedQuarter"
                                    :items="quarters" variant="outlined"
                                    aria-label="Select Incentive Period">
                                    <template v-slot:label>
                                        <LanguagePlaceholder is-html default-text="Select Incentive Period" />
                                    </template>
                                </v-select>
                            </div>
                        </v-col>
                        <v-col cols="12" sm="6" md="4">
                            <div>
                                <v-select clearable :items="countries" return-object v-if="canSelectCountry"
                                    v-model="selectedCountry" class="text-white" item-title="country"
                                    item-value="country" variant="outlined"
                                    aria-label="Select Location">
                                    <template v-slot:label>
                                        <LanguagePlaceholder is-html default-text="Select Location" />
                                    </template>
                                </v-select>
                            </div>
                        </v-col>
                    </v-row>
                </v-row>
                <v-row class="my-2 gap-2" align="center">
                            <div>
                                <v-btn @click="clickSubmit" color="white" variant="outlined"><LanguagePlaceholder default-text="Submit" /></v-btn>
                                <v-btn prepend-icon="mdi-download" @click="downloadFile" color="white" variant="outlined" class="ml-2" aria-label="Download Report"><LanguagePlaceholder default-text="Download" /></v-btn>
                            </div>
                    </v-row>
            </v-col>
            <v-col cols="0" sm="2"></v-col>
        </v-row>
        <v-row justify="center" style="background-color: #ffffff;" class="pb-6">
            <v-container>
                <v-card flat>
                    <v-text-field v-model="searchString" label="Filter" prepend-inner-icon="mdi-filter-outline"
                        variant="outlined" hide-details single-line aria-label="Filter distributor redemption banked rewards data"></v-text-field>

                    <v-data-table items-per-page="50" :search="searchString" :headers="headers" :items="reportData"
                        class="v-data-table" role="table" aria-label="Distributor redemption banked rewards report data">
                        <template v-slot:item.openingBalance="{ item }">
                            {{ $filters.currencyUSD(item.openingBalance) }}
                        </template>
                        <template v-slot:item.banked="{ item }">
                            {{ $filters.currencyUSD(item.banked) }}
                        </template>
                        <template v-slot:item.total="{ item }">
                            {{ $filters.currencyUSD(item.total) }}
                        </template>
                        <template v-slot:item.redeemed="{ item }">
                            {{ $filters.currencyUSD(item.redeemed) }}
                        </template>
                        <template v-slot:item.balance="{ item }">
                            {{ $filters.currencyUSD(item.balance) }}
                        </template>
                    </v-data-table>
                </v-card>
            </v-container>
        </v-row>
    </div>
</template>

<script setup>
import { ref, onMounted, computed } from 'vue'
import LanguagePlaceholder from '@/components/LanguagePlaceholder.vue'
import { useSystemStore } from '@/stores/system'
import { useRegistrationStore } from '@/stores/registration'
import { useExcelStore } from '@/stores/excel'
import PleaseNote from '@/components/PleaseNote.vue'

const systemStore = useSystemStore()
const registrationStore = useRegistrationStore()
const excelStore = useExcelStore()

const countries = ref([])
const reportData = ref([])
const searchString = ref('')

const quarters = ref([])
const selectedQuarter = ref('')
const selectedCountry = ref('')

const headers = [
    { title: 'Partner Name', value: 'partnerName', sortable: true, headerProps: { class: 'header-style' } },
    { title: 'Partner Id', value: 'partnerAffinityId', sortable: true, headerProps: { class: 'header-style' } },
    { title: 'Location', value: 'country', sortable: true, headerProps: { class: 'header-style' } },
    { title: 'Opening Balance', value: 'openingBalance', sortable: true, headerProps: { class: 'header-style' } },
    { title: 'Prev Quarter Banked Rewards', value: 'banked', sortable: true, headerProps: { class: 'header-style' } },
    { title: 'Total (Opening + Banked)', value: 'total', sortable: true, headerProps: { class: 'header-style' } },
    { title: 'Redeemed', value: 'redeemed', sortable: true, headerProps: { class: 'header-style' } },
    { title: 'Balance', value: 'balance', sortable: true, headerProps: { class: 'header-style' } }
]

const canSelectCountry = computed(() => {
    if (systemStore.userDetail.userType.trim() === 'Dell'
        || systemStore.userDetail.userType.trim() === 'TP'
        || systemStore.userDetail.userType.trim() === 'PAM'
        || systemStore.userDetail.userType.trim() === 'Dell Marketing'
        || systemStore.userDetail.userType.trim() === 'TIC_Admin'
    ) {
        return true
    } else {
        return false
    }

})

onMounted(() => {
    systemStore.getQuarters().then(result => {
        if (result.success) {
            quarters.value = result.quarters
                .filter(q => q.currentRedemptionQuarter === true || q.previousRedemptionQuarters === true)
                .map(q => q.quarter)

            selectedQuarter.value = result.quarters.find(q => q.currentRedemptionQuarter === true)?.quarter
        }
    }).catch(error => {
        console.log('recalculateLocalValue', error)
    })

    registrationStore.GetEMEACountries().then(result => {
        countries.value = result.countries
    }).catch(error => {
        console.log('disti dashb get countries', error)
    })
})

const downloadFile = () => {
    if (reportData.value.length === 0) {
        return
    }

    excelStore.generateInMemoryExcelDocument(reportData.value, 'DistributorRedemptionBankedDocument').catch(error => {
        console.log('Generate In Memory Excel Document - DistributorRedemptionBankedDocument', error)
    })
}

const clickSubmit = () => {
    getReportData()
}

const getReportData = () => {
    var country = selectedCountry.value == null || selectedCountry.value == undefined || selectedCountry.value == '' ? '' : selectedCountry.value.country

    systemStore.getDistributorRedemptionBankedData(selectedQuarter.value, country).then(result => {
        reportData.value = result.reportData
    }).catch(error => {
        console.log('distributor redemption banked data, get data', error)
    })
}

</script>

<style>
.header-style {

    background-color: #004D40 !important;
    color: white;
}

.header-style:hover {
    color: white !important;
}

.v-data-table tbody tr:nth-child(even) {
    background-color: #e0e6ec;
    color: #202520;
}

.v-data-table tbody tr:nth-child(odd) {
    background-color: whitesmoke;
    color: #202520;
}
</style>
